// SplashScreen.js
import { useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PropTypes from "prop-types";
import { useTranslation } from "../hooks/useTranslation";

import "../assets/styles/splashScreen.css";
import logo from "../assets/images/logo.png";

export default function SplashScreen({ onFinish }) {
  const t = useTranslation();

  /* ---------- joke logic (unchanged) ---------- */
  const loadingLines = t("common.loadingOptions", { returnObjects: true });
  const loadingMessage = useMemo(() => {
    if (Array.isArray(loadingLines) && loadingLines.length > 0) {
      return loadingLines[Math.floor(Math.random() * loadingLines.length)];
    }
    return t("common.loading");
  }, [loadingLines, t]);
  const baseMessage = useMemo(
    () => loadingMessage.replace(/(\.\.\.|…)+$/, ""),
    [loadingMessage]
  );

  /* ---------- NEW timing logic ---------- */
  useEffect(() => {
    const MIN = 1000;        // your hardcoded minimum
    const HARD = 2500;       // absolute max
    const start = performance.now();
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      const elapsed = performance.now() - start;
      const wait = Math.max(0, MIN - elapsed);
      setTimeout(onFinish, wait);   // ensure MIN
      clearTimeout(hard);
      clearTimeout(tick);
    };

    // fire finish asap (next tick / first paint)
    const tick = setTimeout(finish, 0);            // or requestAnimationFrame(finish)
    const hard = setTimeout(finish, HARD);         // safety cap

    return () => {
      done = true;
      clearTimeout(hard);
      clearTimeout(tick);
    };
  }, [onFinish]);

  /* ---------- UI stays the same ---------- */
  return (
    <AnimatePresence>
      <motion.div
        key="splash"
        className="splash"
        initial={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ opacity: { duration: 0.4, ease: "easeInOut" } }}
      >
        <img src={logo} alt="Logo" className="logo" />

        <motion.p
          className="splashLoading-text"
          animate={{ opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
        >
          {baseMessage}
          {Array.from({ length: 3 }).map((_, idx) => (
            <motion.span
              key={idx}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0] }}
              transition={{
                repeat: Infinity,
                duration: 1.2,
                ease: "easeInOut",
                delay: idx * 0.2,
              }}
            >
              .
            </motion.span>
          ))}
        </motion.p>

        <motion.div
          initial={{ rotate: 0 }}
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          style={{
            width: 40,
            height: 40,
            border: "4px solid var(--mainColor_gold)",
            borderTopColor: "transparent",
            borderRadius: "50%",
            margin: "1.5rem 0",
          }}
        />
      </motion.div>
    </AnimatePresence>
  );
}

SplashScreen.propTypes = { onFinish: PropTypes.func.isRequired };
